/**
 * ApprovedGasTariffSelector - Two-tier selection for approved natural gas tariffs
 *
 * First selects utility, then selects specific rate from that utility.
 * Fetches from /api/site_dna/v1/projects/custom-rates with tariff_type=natural_gas filter
 * Returns only approved natural gas tariffs
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Box,
    Typography
} from '@mui/material';
import axios from 'axios';
import { useAuth, API_BASE_URL } from '@vortx/shared-ui';

/**
 * ApprovedGasTariffSelector Component
 *
 * @param {string} value - Currently selected tariff ID
 * @param {Function} onChange - Callback when tariff is selected (tariffId)
 * @param {string} projectId - Project ID (optional, for future filtering)
 */
export default function ApprovedGasTariffSelector({ value, onChange, projectId }) {
    const { currentUser } = useAuth();
    const [tariffs, setTariffs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedUtility, setSelectedUtility] = useState('');

    useEffect(() => {
        loadApprovedGasTariffs();
    }, [projectId]);

    // When value changes from parent (e.g., loading saved scenario), update selectedUtility
    // Only sync when a valid tariff is selected, not when clearing
    useEffect(() => {
        if (value && tariffs.length > 0) {
            const selectedTariff = tariffs.find(t => t.id === value);
            if (selectedTariff && selectedTariff.utility_name) {
                setSelectedUtility(selectedTariff.utility_name);
            }
        }
        // Don't clear selectedUtility when value becomes empty - let user control it
    }, [value, tariffs]);

    const loadApprovedGasTariffs = async () => {
        setLoading(true);
        setError(null);

        try {
            // Get auth headers
            const headers = { 'Content-Type': 'application/json' };
            if (currentUser) {
                const token = await currentUser.getIdToken();
                headers['Authorization'] = `Bearer ${token}`;
            } else {
                headers['X-Dev-User-Id'] = 'tim@vortx.us';
            }

            // Fetch tariffs with natural_gas filter
            const response = await axios.get(
                `${API_BASE_URL}/api/site_dna/v1/projects/custom-rates?tariff_type=natural_gas`,
                { headers }
            );

            // Extract rates from response
            const rates = response.data.rates || [];

            // Filter to approved tariffs only
            const approvedTariffs = rates.filter(rate => rate.status === 'approved');
            setTariffs(approvedTariffs);
        } catch (err) {
            console.error('Failed to load gas tariffs:', err);
            setError('Failed to load gas tariffs');
        } finally {
            setLoading(false);
        }
    };

    // Group tariffs by utility
    const utilitiesWithTariffs = useMemo(() => {
        const utilityMap = new Map();
        tariffs.forEach(tariff => {
            const utilityName = tariff.utility_name || 'Unknown Utility';
            if (!utilityMap.has(utilityName)) {
                utilityMap.set(utilityName, []);
            }
            utilityMap.get(utilityName).push(tariff);
        });
        return utilityMap;
    }, [tariffs]);

    // Get available utilities (sorted)
    const utilities = useMemo(() => {
        return Array.from(utilitiesWithTariffs.keys()).sort();
    }, [utilitiesWithTariffs]);

    // Get tariffs for selected utility
    const availableRates = useMemo(() => {
        if (!selectedUtility) return [];
        return utilitiesWithTariffs.get(selectedUtility) || [];
    }, [selectedUtility, utilitiesWithTariffs]);

    const handleUtilityChange = (utilityName) => {
        setSelectedUtility(utilityName);
        // Clear rate selection when utility changes
        onChange('');
    };

    const handleRateChange = (tariffId) => {
        onChange(tariffId);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading gas tariffs...</Typography>
            </Box>
        );
    }

    if (error) {
        return <Typography color="error">{error}</Typography>;
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Utility Selector */}
            <FormControl fullWidth>
                <InputLabel>Select Utility</InputLabel>
                <Select
                    value={selectedUtility || ''}
                    onChange={(e) => handleUtilityChange(e.target.value)}
                    label="Select Utility"
                >
                    <MenuItem value="">
                        <em>None (use simple rate)</em>
                    </MenuItem>
                    {utilities.map((utilityName) => (
                        <MenuItem key={utilityName} value={utilityName}>
                            {utilityName}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* Rate Selector - only shown when utility is selected */}
            {selectedUtility && (
                <FormControl fullWidth>
                    <InputLabel>Select Rate Schedule</InputLabel>
                    <Select
                        value={value || ''}
                        onChange={(e) => handleRateChange(e.target.value)}
                        label="Select Rate Schedule"
                    >
                        <MenuItem value="">
                            <em>Select a rate...</em>
                        </MenuItem>
                        {availableRates.map((tariff) => (
                            <MenuItem key={tariff.id} value={tariff.id}>
                                {tariff.name || tariff.tariff_name}
                                {tariff.rate_schedule_name && ` (${tariff.rate_schedule_name})`}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
        </Box>
    );
}
